import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import useLogout from "components/common/useLogout";
import GenericModal from "components/common/GenericModal";
import { ICONS } from "components/common/constants/swal.constants";
import "./OfficeKioskLayout.css";
import "./AppointmentGroups.css";
import { ClientDataContext } from "components/context/ClientDataContext";

interface OfficeKioskLayoutProps {
  currentUser: JObj;
  children?: React.ReactNode;
}

const OfficeKioskLayout: React.FC<OfficeKioskLayoutProps> = ({
  currentUser,
  children,
}) => {
  const history = useHistory();
  const clientCtx = React.useContext(ClientDataContext);
  const dispatchAction = clientCtx?.dispatchAction;

  const [genericModalData, setGenericModalData] = useState<{
    show: boolean;
    title?: string;
    subtext?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icon?: any;
  }>({ show: false });

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const IDLE_RESET_MS = 3 * 60 * 1000; // 3 minutes

    const resetIdleTimer = () => {
      if (idleTimeoutRef.current != null) {
        window.clearTimeout(idleTimeoutRef.current);
      }

      idleTimeoutRef.current = window.setTimeout(() => {
        if (dispatchAction) {
          dispatchAction({
            type: "RESET_KIOSK_UI",
            payload: {} as JObj,
          });
        }

        console.log("kiosk: auto-scrolling back to top...");
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, IDLE_RESET_MS);
    };

    const windowEvents: Array<keyof WindowEventMap> = [
      "mousemove",
      "keydown",
      "touchstart",
      "click",
    ];

    windowEvents.forEach((evt) => window.addEventListener(evt, resetIdleTimer));

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", resetIdleTimer);
    }

    resetIdleTimer();

    return () => {
      if (idleTimeoutRef.current != null) {
        window.clearTimeout(idleTimeoutRef.current);
      }
      windowEvents.forEach((evt) =>
        window.removeEventListener(evt, resetIdleTimer)
      );
      if (container) {
        container.removeEventListener("scroll", resetIdleTimer);
      }
    };
  }, [dispatchAction]);

  const showLogoutSuccess = () => {
    setGenericModalData({
      show: true,
      title: "Goodbye",
      subtext: "See you next time!",
      icon: ICONS.checkMark,
    });
  };

  const showLogoutError = () => {
    setGenericModalData({
      show: true,
      title: "Oops!",
      subtext: "Some error occurred",
      icon: ICONS.error,
    });
  };

  const hideModal = () => {
    setGenericModalData((prev) => ({ ...prev, show: false }));
  };

  const logOut = useLogout(
    "/login",
    showLogoutSuccess,
    showLogoutError,
    hideModal
  );

  useEffect(() => {
    if (!currentUser || currentUser.roleId !== 9) {
      history.replace("/login");
    }
  }, [currentUser, history]);

  if (!currentUser || currentUser.roleId !== 9) {
    return null;
  }

  return (
    <div
      className="ok-layout-root ok-small-scrollbar"
      style={{ height: "100vh", overflowY: "scroll" }}
      ref={scrollContainerRef}
    >
      {genericModalData.show && (
        <GenericModal
          show={genericModalData.show}
          onHide={hideModal}
          title={genericModalData.title || ""}
          subtext={genericModalData.subtext || ""}
          icon={genericModalData.icon}
          hideBtn
        />
      )}

      <div className="ok-layout-main">{children}</div>

      <footer className="ok-layout-footer">
        <span className="ok-footer-text">MyRep Office Kiosk</span>
        <button
          className="ok-footer-logout-btn"
          onClick={() => logOut()}
          data-cy="kiosk-logout-btn"
        >
          Log out
        </button>
      </footer>
    </div>
  );
};

export default OfficeKioskLayout;
